import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css'; // Import default Tippy.js styles
import styles from './tooltipnew.module.scss'; // Import custom SCSS styles
import {
  ReactElement,
  useEffect,
  useRef,
  useState,
  KeyboardEventHandler,
  FocusEventHandler,
  ReactNode,
} from 'react';
import { handleKeyDown, keyCodes } from '@msc-b2c/shared-utils/keyboardUtils';

interface TooltipProps {
  children: ReactElement;
  content: ReactNode;
  appliedCssClassNames?: string;
  positionStyle: 'top' | 'bottom' | 'left' | 'right';
  zIndex?: number;
  triggerAriaLabel?: string;
}

export const TooltipNew = ({
  children,
  content,
  appliedCssClassNames,
  positionStyle,
  zIndex,
  triggerAriaLabel,
}: TooltipProps) => {
  const tooltipRef = useRef<any>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const triggerAriaLabelText = triggerAriaLabel?.trim() || undefined;

  const handleOnShow = (instance: any) => {
    setIsVisible(true);
    if (instance.popper) {
      const tippyBox = instance.popper.querySelector('.tippy-box');
      if (tippyBox) {
        tippyBox.setAttribute('tabindex', '0');
        tippyBox.setAttribute('role', 'tooltip');
      }
    }
  };

  const blurTriggerFocus = () => {
    const activeElement = document.activeElement;

    if (activeElement instanceof HTMLElement && triggerRef.current?.contains(activeElement)) {
      activeElement.blur();
    }
  };

  const hideTooltip = () => {
    setIsVisible(false);
    tooltipRef.current?.hide();
    blurTriggerFocus();
  };

  const handleHidden = () => {
    setIsVisible(false);
    blurTriggerFocus();
  };

  const showTooltip = () => {
    setIsVisible(true);
    tooltipRef.current?.show();
  };

  const handleTriggerKeyDown: KeyboardEventHandler = (event) => {
    if (event.key === keyCodes.ESCAPE) {
      if (tooltipRef?.current?.state.isVisible) {
        event.preventDefault();
        hideTooltip();
      }
      return;
    }

    if (!tooltipRef?.current?.state.isVisible) {
      handleKeyDown(event, showTooltip);
    }
  };

  const handleOnBlur: FocusEventHandler = (event) => {
    const relatedTarget = event.relatedTarget as Node | null;
    const popper = tooltipRef.current?.popper;

    if (relatedTarget && popper?.contains(relatedTarget)) {
      return;
    }

    hideTooltip();
  };

  const handleMouseEnter = () => {
    showTooltip();
  };

  const handleMouseLeave = () => {
    hideTooltip();
  };

  useEffect(() => {
    const isToolTipVisible = () => tooltipRef?.current?.state.isVisible;

    const handleScroll = () => {
      if (isVisible || isToolTipVisible()) {
        hideTooltip();
      }
    };

    window.addEventListener('scroll', handleScroll, true);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isVisible]);

  return (
    <Tippy
      content={content}
      visible={isVisible}
      className={`${styles['tooltipnew-container']} ${appliedCssClassNames}`}
      placement={positionStyle}
      arrow={true}
      interactive={true}
      zIndex={zIndex}
      onShow={handleOnShow}
      onHide={handleHidden}
      onHidden={handleHidden}
      onCreate={(instance) => (tooltipRef.current = instance)}
    >
      <div
        ref={triggerRef}
        role="button"
        tabIndex={0}
        aria-label={triggerAriaLabelText}
        aria-expanded={isVisible}
        onBlur={handleOnBlur}
        onFocus={showTooltip}
        onKeyDown={handleTriggerKeyDown}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </div>
    </Tippy>
  );
};
